// The money decisions behind "Correct this payment" — kept out of the
// component so they can be tested directly. Every rule here is about not
// destroying a record by accident.

import { isKnownMode } from '../../constants/paymentModes';

/** The sale as the form should first show it. */
export const initialForm = (sale) => ({
  total: sale.total_amount ? String(Number(sale.total_amount)) : '',
  ptype: sale.payment_type === 'advance' ? 'advance' : 'full',
  advance: sale.advance_amount ? String(Number(sale.advance_amount)) : '',
  // A mode outside the known list (or none) starts UNSELECTED. Pre-selecting
  // cash is how a mode nobody checked became a fact.
  mode: isKnownMode(sale.payment_mode) ? sale.payment_mode : '',
  reason: '',
});

const sameMoney = (typed, stored) => {
  // Compare what the numbers MEAN, not how they were typed: "500.00", "0500"
  // and "500" are the same five hundred rupees, and re-typing one must not
  // count as a correction that needs explaining.
  if (String(typed).trim() === '') return String(stored || '') === '';
  const a = Number(typed);
  const b = Number(stored === '' || stored == null ? NaN : stored);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
};

/** What the admin has actually changed, field by field. */
export const whatChanged = (sale, form) => {
  const start = initialForm(sale);
  const price = !sameMoney(form.total, start.total);
  const type = form.ptype !== start.ptype;
  const advance = form.ptype === 'advance' && !sameMoney(form.advance, start.advance);
  const mode = form.mode !== start.mode;
  return { price, type, advance, mode, any: price || type || advance || mode };
};

/**
 * The sale is an advance whose balance was already collected, and the admin
 * is switching it to a full payment. The collection is a real event the day's
 * book counted — it has to be reopened on its own before the terms may move,
 * or flipping back and forth would erase it for good. The backend refuses
 * this too; saying it here saves a round trip and explains it in place.
 */
export const blocksOnCollectedBalance = (sale, form) =>
  Boolean(sale.balance_paid)
  && sale.payment_type === 'advance'
  && form.ptype === 'full';

/**
 * The body to PATCH, or an { error } the admin must resolve first.
 * Fields the admin never touched are LEFT OUT — a key that isn't sent is a
 * value the backend won't overwrite.
 */
export const buildCorrectionBody = (sale, form) => {
  const total = Number(form.total);
  if (!Number.isFinite(total) || total <= 0) {
    return { error: 'Enter the correct total' };
  }
  if (blocksOnCollectedBalance(sale, form)) {
    return {
      error: 'This sale records a balance already collected. Reopen that collection first '
        + 'if it never happened, then change the terms.',
    };
  }

  const changed = whatChanged(sale, form);
  const body = { total_amount: String(total), payment_type: form.ptype };

  if (form.ptype === 'advance') {
    const advance = Number(form.advance);
    if (!Number.isFinite(advance) || advance <= 0) {
      return { error: 'Enter the advance collected' };
    }
    if (advance >= total) {
      return {
        error: 'An advance must be less than the total — switch to Full payment above '
          + 'if the whole amount was taken',
      };
    }
    body.advance_amount = String(advance);
  }

  // Only send a mode the admin actually chose. A sale carrying a legacy value
  // ('net_banking') shows nothing selected, and a blank sent back would wipe
  // what is on the record — while fixing a price, without anyone noticing.
  if (form.mode) body.payment_mode = form.mode;

  if (changed.any) {
    if (!String(form.reason).trim()) {
      return { error: 'Say what you are correcting — the superadmin sees this note' };
    }
    body.reason = String(form.reason).trim();
  }
  return { body };
};
